import { humanizePath } from "@/components/layout-2/filter-tree/tree-utils";
import { Filter } from "@/components/layout/types";
import { formatDateValue } from "@/lib/utils";
import { AnyDRQQuery, DRQBuilder } from "@/utils/drq";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";

export interface FilterInstance {
    id: string;
    field_name: string;
    value: string;
    operator: string;
    type?: string;
}

export function useObjectListFilters() {
    const [drqFilterState, setDRQFilterState] = useState<FilterInstance[]>([]);
    const [filterLogic, setFilterLogic] = useState<string>("");
    const [currentDRQQuery, setCurrentDRQQuery] = useState<AnyDRQQuery | {}>({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Load filters from DRQ query (used when loading saved views)
    const loadFiltersFromDRQ = useCallback(
        (drqQuery: AnyDRQQuery | {}, forceLoad: boolean = false) => {
            // If we have unsaved changes and this is not a forced load, don't overwrite
            if (hasUnsavedChanges && !forceLoad) {
                console.log("🔶 Skipping filter load - unsaved changes detected");
                return;
            }
            // Ne recharge que si le DRQ a changé
            if (JSON.stringify(drqQuery) === JSON.stringify(currentDRQQuery)) {
                return;
            }
            if (Object.keys(drqQuery).length === 0) {
                setDRQFilterState([]);
                setFilterLogic("");
                setCurrentDRQQuery({});
                setHasUnsavedChanges(false);
                return;
            }
            console.log("🟪 drqQuery", drqQuery);
            const filterInstances = DRQBuilder.extractFilterInstancesFromDRQ(drqQuery);
            console.log("🟪 filterInstances", filterInstances);

            // Reconstruct filter logic expression if the DRQ is a flat OR query.
            // Complex nested structures (e.g. OR(AND(f1,f2), f3)) are not reconstructed
            // to avoid silently altering the semantics.
            const reconstructedLogic = DRQBuilder.isDRQFlatOrQuery(drqQuery)
                ? DRQBuilder.reconstructFlatOrLogicExpression(filterInstances.length)
                : "";

            setDRQFilterState(filterInstances);
            setFilterLogic(reconstructedLogic);
            // Set currentDRQQuery immediately to trigger data fetch without delay
            setCurrentDRQQuery(drqQuery);
            setHasUnsavedChanges(false);
        },
        [hasUnsavedChanges, currentDRQQuery]
    );

    // Build DRQ query when filter state or logic changes
    useEffect(() => {
        if (drqFilterState.length > 0) {
            const newDRQQuery = DRQBuilder.buildFromFilterInstancesWithLogic(drqFilterState, filterLogic);
            const newDRQQueryStr = JSON.stringify(newDRQQuery || {});
            const currentDRQQueryStr = JSON.stringify(currentDRQQuery);
            // Only update if the query has actually changed
            if (newDRQQueryStr !== currentDRQQueryStr) {
                setCurrentDRQQuery(newDRQQuery || {});
            }
        } else {
            // Only update if currentDRQQuery is not already empty
            if (Object.keys(currentDRQQuery).length > 0) {
                setCurrentDRQQuery({});
            }
        }
    }, [drqFilterState, filterLogic, currentDRQQuery]);

    // Add a filter
    const handleAddFilter = useCallback((filter: Filter, value: string, operator: string) => {
        const newFilter: FilterInstance = {
            id: `${filter.field_name}_${Date.now()}_${Math.random()}`,
            field_name: filter.field_name,
            value,
            operator,
            type: filter.type
        };
        setDRQFilterState(prev => [...prev, newFilter]);
        setHasUnsavedChanges(true);
    }, []);

    // Update an existing filter while keeping its position in the array
    const handleUpdateFilter = useCallback((filterId: string, filter: Filter, value: string, operator: string) => {
        setDRQFilterState(prev =>
            prev.map(f =>
                f.id === filterId
                    ? {
                          ...f,
                          field_name: filter.field_name,
                          value,
                          operator,
                          type: filter.type
                      }
                    : f
            )
        );
        setHasUnsavedChanges(true);
    }, []);

    // Remove a filter by ID
    const handleRemoveFilter = useCallback((filterId: string) => {
        setDRQFilterState(prev => prev.filter(f => f.id !== filterId));
        setHasUnsavedChanges(true);
    }, []);

    // Clear all filters
    const handleClearAllFilters = useCallback(() => {
        setDRQFilterState([]);
        setHasUnsavedChanges(true);
    }, []);

    // Setter for logic
    const handleSetFilterLogic = useCallback((logic: string) => {
        setFilterLogic(logic);
        setHasUnsavedChanges(true);
    }, []);

    // Mark filters as saved (clears unsaved changes flag)
    const markFiltersSaved = useCallback(() => {
        setHasUnsavedChanges(false);
    }, []);

    // Get active filters for display
    const getActiveFilters = useCallback(
        (allFilters: Filter[], searchValue?: string) => {
            const activeFiltersDisplay: { name: string; value: string; id: string; isExclude: boolean }[] = [];

            // Helper function to check if an operator is an exclude operator
            const isExcludeOperator = (operator: string) => {
                return ["not_contains", "not_equal_to"].includes(operator);
            };

            // Helper function to format date values
            const formatDateValueFilters = (value: string, filterType?: string) => {
                if (filterType === "date-range" || filterType === "datetime-range") {
                    // Check if it's a range (contains comma)
                    if (value.includes(",")) {
                        const [startDate, endDate] = value.split(",").map(v => v.trim());
                        try {
                            const start = DateTime.fromISO(startDate);
                            const end = DateTime.fromISO(endDate);
                            if (start.isValid && end.isValid) {
                                return `${formatDateValue(start, "short")} to ${formatDateValue(end, "short")}`;
                            }
                        } catch (error) {
                            // If parsing fails, return original value
                        }
                    } else {
                        // Single date value
                        try {
                            const date = DateTime.fromISO(value);
                            if (date.isValid) {
                                return formatDateValue(date, "short");
                            }
                        } catch (error) {
                            // If parsing fails, return original value
                        }
                    }
                }
                return value;
            };

            // Process DRQ filters
            drqFilterState.forEach(filterInstance => {
                // A field of a linked object (`registration__email`) has no definition of the list.
                const filter =
                    allFilters.find((f: Filter) => f.field_name === filterInstance.field_name) ??
                    (filterInstance.field_name.includes("__")
                        ? ({ field_name: filterInstance.field_name, name: humanizePath(filterInstance.field_name), type: filterInstance.type } as Filter)
                        : undefined);
                if (filter && (filterInstance.value || filterInstance.operator === "is_empty" || filterInstance.operator === "is_not_empty")) {
                    const filterName = filter.name || filter.field_name;

                    let displayValue = filterInstance.value;
                    // Correction : afficher True/False pour is_empty/is_not_empty sur date/datetime
                    if (["is_empty", "is_not_empty"].includes(filterInstance.operator)) {
                        displayValue = filterInstance.operator === "is_empty" ? "True" : "False";
                    } else if (filter.type === "checkbox") {
                        displayValue = filterInstance.value === "True" ? "True" : "False";
                    } else if (filter.type === "select" && filter.options) {
                        const option = filter.options.find((opt: { value: string; label?: string }) => opt.value === filterInstance.value);
                        displayValue = option?.label || filterInstance.value;
                    } else if (filter.type === "combobox" && filter.options) {
                        const option = filter.options.find((opt: { value: string; label?: string }) => opt.value === filterInstance.value);
                        displayValue = option?.label || filterInstance.value;
                    } else {
                        // Format date values
                        displayValue = formatDateValueFilters(filterInstance.value, filter.type);
                    }

                    activeFiltersDisplay.push({
                        name: filterName,
                        value: displayValue,
                        id: filterInstance.id,
                        isExclude: isExcludeOperator(filterInstance.operator)
                    });
                }
            });

            // Add search filter from URL if it exists
            if (searchValue) {
                activeFiltersDisplay.push({
                    name: "Recherche",
                    value: `"${searchValue}"`,
                    id: "search",
                    isExclude: false
                });
            }

            return { activeFilters: drqFilterState, activeFiltersDisplay };
        },
        [drqFilterState]
    );

    return {
        drqFilterState,
        filterLogic,
        currentDRQQuery,
        hasUnsavedChanges,
        loadFiltersFromDRQ,
        handleAddFilter,
        handleUpdateFilter,
        handleRemoveFilter,
        handleClearAllFilters,
        handleSetFilterLogic,
        markFiltersSaved,
        getActiveFilters
    };
}
